import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";

// Feature bullets are the QUALITATIVE per-plan features. Seat, profile, and
// PD-generator counts are appended dynamically from plan.max_managers /
// plan.max_employees so the Pricing page always reflects what the Platform
// Owner has configured.
const FEATURES = {
  free_trial: [
    "AI task extraction from PDs",
    "Blind two-sided ratings",
    "Side-by-side comparison overlay",
    "Magic-link employee invites",
    "Free forever — no credit card required",
  ],
  small_biz: [
    "Everything in Free",
    "360° multi-rater reviews — peers, direct reports, manager, self",
    "Blind-to-open vault with anonymised cohort scores",
    "AI Peer Consensus — Claude summarises strengths, growth areas & cohort divergences",
    "Private employee share — radar + weighted overall, comments withheld",
    "Annual PD Generator quota resets every year",
    "Org Admin approval workflow",
    "Audit log retention (90 days)",
    "Email support",
  ],
  growth: [
    "Everything in Small Business",
    "Priority email support",
    "Unlimited 360° review cycles per employee",
  ],
  enterprise: [
    "Everything in Growth",
    "Volume pricing — talk to us",
    "SSO &amp; custom legal agreements (on request)",
    "Bespoke 360° cohort weighting & threshold tuning",
  ],
};

// Format big numbers with a thousands separator (e.g., 10,000)
function fmt(n) {
  const v = Number(n || 0);
  return v >= 1000 ? v.toLocaleString("en-AU") : String(v);
}

// PD Generator quota copy per plan. Rules (see backend consume_pd_generation):
//   • Free plan → 1 LIFETIME PD generation, no reset.
//   • Any paid plan → quota == plan.max_employees per anniversary year.
function pdQuotaBullet(plan) {
  const slug = plan.slug || "";
  const isFree = plan.is_default_trial || slug === "free_trial";
  if (isFree) return "AI PD Generator — 1 free draft (lifetime)";
  const n = plan.max_employees ?? 0;
  return `AI PD Generator — ${fmt(n)} AI-drafted PDs/year`;
}

function planLimitBullets(plan) {
  const mgrs = plan.max_managers ?? 0;
  const emps = plan.max_employees ?? 0;
  return [
    `${fmt(mgrs)} manager ${mgrs === 1 ? "seat" : "seats"}`,
    `Up to ${fmt(emps)} employee ${emps === 1 ? "profile" : "profiles"}`,
    pdQuotaBullet(plan),
  ];
}

function PlanTile({ plan, currentPlanId, onCheckout, isLoggedInAdmin, isHighlighted }) {
  const isCurrent = plan.id === currentPlanId;
  const isFree = (plan.monthly_cost ?? 0) <= 0;
  const isEnterprise = plan.slug === "enterprise";
  // Dynamic limit lines FIRST, then the qualitative bullets — matches the
  // reading order most users scan (numbers → benefits).
  const features = [...planLimitBullets(plan), ...(FEATURES[plan.slug] || [])];

  let cta;
  if (isCurrent) cta = <span className="font-mono-cal text-xs uppercase text-[#00A36C]">[YOUR CURRENT PLAN]</span>;
  else if (isEnterprise) cta = <a href="mailto:support@calibrate.management?subject=CALIBRATE%20Enterprise%20pricing" className="cal-btn w-full justify-center" data-testid={`cta-${plan.slug}`}>Contact sales</a>;
  else if (isFree) cta = <Link to="/register" className="cal-btn w-full justify-center" data-testid={`cta-${plan.slug}`}>Start free →</Link>;
  else if (isLoggedInAdmin) cta = <button onClick={() => onCheckout(plan)} className="cal-btn w-full justify-center" data-testid={`cta-${plan.slug}`}>Upgrade now →</button>;
  // Anonymous visitors viewing a PAID plan: registration creates them on the
  // Free (default_trial) plan first, then they upgrade from /admin/org.
  // Saying "Start free" here would misrepresent this tile — use "Get started"
  // so the copy stays honest about the paid tier.
  else cta = <Link to="/register" className="cal-btn w-full justify-center" data-testid={`cta-${plan.slug}`}>Get started →</Link>;

  return (
    <div
      className={`cal-card p-6 flex flex-col ${isHighlighted ? "border-[#FF4F00] border-2" : ""}`}
      data-testid={`pricing-tile-${plan.slug}`}
    >
      {isHighlighted && (
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-3 flex items-center gap-1">
          <Sparkles size={11}/> Most popular
        </div>
      )}
      <div className="font-display font-bold text-2xl">{plan.name}</div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display font-bold text-5xl">A${plan.monthly_cost}</span>
        <span className="text-sm text-[#666]">/month</span>
      </div>
      {isFree ? (
        <div className="mt-2 font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">
          [FREE FOREVER · NO CARD]
        </div>
      ) : plan.trial_days > 0 && (
        <div className="mt-2 font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">
          [{plan.trial_days}-day free trial]
        </div>
      )}
      <ul className="mt-6 space-y-2 text-sm flex-1" data-testid={`pricing-features-${plan.slug}`}>
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check size={14} className="mt-0.5 shrink-0 text-[#FF4F00]" />
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(f) }} />
          </li>
        ))}
      </ul>
      <div className="mt-6">{cta}</div>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/billing/plans/public");
        setPlans(r.data);
        if (user?.is_org_admin) {
          try {
            const orgRes = await api.get("/org/me");
            setMe(orgRes.data);
          } catch { /* non-fatal */ }
        }
      } catch (e) {
        toast.error(apiError(e, "Could not load plans"));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleCheckout = async (plan) => {
    if (!user) { navigate("/register"); return; }
    if (!user.is_org_admin) { toast.error("Only Org Admins can change the subscription"); return; }
    try {
      const r = await api.post("/billing/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiError(e, "Could not start checkout"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">[PRICING]</div>
      <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tight mb-4">
        Simple monthly pricing.
      </h1>
      <p className="text-[#444] text-lg max-w-2xl mb-12">
        Start on the Free plan — free forever, no credit card required. Upgrade whenever you
        outgrow it. Cancellation is one click and you keep access until the end of the period.
      </p>

      {loading ? (
        <div className="font-mono-cal text-xs text-[#666]">[LOADING PLANS…]</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-grid">
          {/* Enterprise is always rendered last / far right (Iter42c) */}
          {[...plans].sort((a, b) => {
            if (a.slug === "enterprise") return 1;
            if (b.slug === "enterprise") return -1;
            return 0;
          }).map((p) => (
            <PlanTile
              key={p.id}
              plan={p}
              currentPlanId={me?.plan?.id}
              onCheckout={handleCheckout}
              isLoggedInAdmin={!!user?.is_org_admin}
              isHighlighted={p.slug === "small_biz"}
            />
          ))}
        </div>
      )}

      <section className="mt-20 max-w-3xl text-sm text-[#444]">
        <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Billing details you should know</h2>
        <ul className="space-y-2">
          <li className="flex items-start gap-2"><Check size={14} className="mt-1 text-[#FF4F00] shrink-0"/> All paid plans auto-renew monthly via Stripe.</li>
          <li className="flex items-start gap-2"><Check size={14} className="mt-1 text-[#FF4F00] shrink-0"/> One-click cancellation from your Org Admin dashboard. Access continues until the end of your billing period.</li>
          <li className="flex items-start gap-2"><Check size={14} className="mt-1 text-[#FF4F00] shrink-0"/> Charged in Australian Dollars. Stripe handles GST where applicable.</li>
          <li className="flex items-start gap-2"><Check size={14} className="mt-1 text-[#FF4F00] shrink-0"/> No setup fees, no per-employee surcharges, no long-term lock-in.</li>
        </ul>
      </section>

      <footer className="mt-24 pt-10 border-t border-[#111]/15 flex flex-wrap items-center justify-between gap-4 text-xs font-mono-cal text-[#666]">
        <Link to="/" className="hover:text-[#FF4F00]">← Back to home</Link>
        <Link to="/terms" className="hover:text-[#FF4F00]" data-testid="pricing-terms-link">Terms &amp; Conditions</Link>
      </footer>
    </div>
  );
}
